// Webhook feature logic — framework-agnostic, zero React imports.

import type { WebhookEndpoint } from '@core/types/webhook';
import type { WebhookEndpointStatus } from '@core/types/webhook';
import type { WebhookDeliveryStatus } from '@core/types/webhook';
import type { WebhookAuthType } from '@core/types/webhook';

// ============================================
// Endpoint status display
// ============================================

export const endpointStatusMap: Record<string, { labelKey: string; variant: 'success' | 'error' | 'warning' | 'default' }> = {
  WEBHOOK_ENDPOINT_STATUS_UNSPECIFIED: { labelKey: 'webhooks.statusUnknown', variant: 'default' },
  WEBHOOK_ENDPOINT_STATUS_ACTIVE: { labelKey: 'webhooks.statusActive', variant: 'success' },
  WEBHOOK_ENDPOINT_STATUS_DISABLED: { labelKey: 'webhooks.statusDisabled', variant: 'default' },
  WEBHOOK_ENDPOINT_STATUS_SUSPENDED: { labelKey: 'webhooks.statusSuspended', variant: 'warning' },
  WEBHOOK_ENDPOINT_STATUS_DELETED: { labelKey: 'webhooks.statusDeleted', variant: 'error' },
};

export function getEndpointStatusDisplay(status?: WebhookEndpointStatus | string): { labelKey: string; variant: 'success' | 'error' | 'warning' | 'default' } {
  return endpointStatusMap[status ?? 'WEBHOOK_ENDPOINT_STATUS_UNSPECIFIED'] ?? endpointStatusMap.WEBHOOK_ENDPOINT_STATUS_UNSPECIFIED;
}

// ============================================
// Delivery status display
// ============================================

export const deliveryStatusMap: Record<string, { labelKey: string; variant: 'success' | 'error' | 'warning' | 'default' | 'info' }> = {
  WEBHOOK_DELIVERY_STATUS_UNSPECIFIED: { labelKey: 'webhooks.deliveryUnknown', variant: 'default' },
  WEBHOOK_DELIVERY_STATUS_PENDING: { labelKey: 'webhooks.deliveryPending', variant: 'warning' },
  WEBHOOK_DELIVERY_STATUS_SENDING: { labelKey: 'webhooks.deliverySending', variant: 'info' },
  WEBHOOK_DELIVERY_STATUS_SUCCESS: { labelKey: 'webhooks.deliverySuccess', variant: 'success' },
  WEBHOOK_DELIVERY_STATUS_FAILED: { labelKey: 'webhooks.deliveryFailed', variant: 'error' },
  WEBHOOK_DELIVERY_STATUS_RETRYING: { labelKey: 'webhooks.deliveryRetrying', variant: 'warning' },
  WEBHOOK_DELIVERY_STATUS_EXHAUSTED: { labelKey: 'webhooks.deliveryExhausted', variant: 'error' },
};

export function getDeliveryStatusDisplay(status?: WebhookDeliveryStatus | string): { labelKey: string; variant: 'success' | 'error' | 'warning' | 'default' | 'info' } {
  return deliveryStatusMap[status ?? 'WEBHOOK_DELIVERY_STATUS_UNSPECIFIED'] ?? deliveryStatusMap.WEBHOOK_DELIVERY_STATUS_UNSPECIFIED;
}

// ============================================
// Auth type labels
// ============================================

export const authTypeLabels: Record<string, string> = {
  WEBHOOK_AUTH_TYPE_UNSPECIFIED: 'webhooks.authNone',
  WEBHOOK_AUTH_TYPE_HMAC: 'HMAC SHA-256',
  WEBHOOK_AUTH_TYPE_HEADER: 'Custom Header',
  WEBHOOK_AUTH_TYPE_BASIC: 'Basic Auth',
  WEBHOOK_AUTH_TYPE_BEARER: 'Bearer Token',
  WEBHOOK_AUTH_TYPE_NONE: 'webhooks.authNone',
};

export function getAuthTypeLabel(authType?: WebhookAuthType | string): string {
  return authTypeLabels[authType ?? 'WEBHOOK_AUTH_TYPE_NONE'] ?? 'None';
}

export function needsAuthConfig(authType?: WebhookAuthType | string): boolean {
  return authType !== 'WEBHOOK_AUTH_TYPE_NONE' && authType !== 'WEBHOOK_AUTH_TYPE_UNSPECIFIED';
}

// ============================================
// Create form
// ============================================

export interface CreateEndpointFormState {
  name: string;
  description: string;
  url: string;
  authType: string;
  authHeader: string;
  authValue: string;
  subscribeAll: boolean;
  eventTypes: string[];
}

export const EMPTY_CREATE_FORM: CreateEndpointFormState = {
  name: '',
  description: '',
  url: '',
  authType: 'WEBHOOK_AUTH_TYPE_HMAC',
  authHeader: '',
  authValue: '',
  subscribeAll: true,
  eventTypes: [],
};

export function validateCreateForm(data: CreateEndpointFormState): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!data.name.trim()) {
    errors.name = 'webhooks.validation.nameRequired';
  } else if (data.name.length > 100) {
    errors.name = 'webhooks.validation.nameTooLong';
  }
  if (!data.url.trim()) {
    errors.url = 'webhooks.validation.urlRequired';
  } else {
    try {
      const parsed = new URL(data.url);
      if (parsed.protocol !== 'https:') {
        errors.url = 'webhooks.validation.urlHttps';
      }
    } catch {
      errors.url = 'webhooks.validation.urlInvalid';
    }
  }
  if (!data.subscribeAll && data.eventTypes.length === 0) {
    errors.eventTypes = 'webhooks.validation.eventTypesRequired';
  }
  return errors;
}

// ============================================
// Formatters
// ============================================

export function formatDate(iso?: string): string {
  if (!iso) return '---';
  const date = new Date(iso);
  if (isNaN(date.getTime())) return '---';
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

export function formatDateTime(iso?: string): string {
  if (!iso) return '---';
  const date = new Date(iso);
  if (isNaN(date.getTime())) return '---';
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

export function formatUrl(url: string): string {
  try {
    const parsed = new URL(url);
    return `${parsed.host}${parsed.pathname}`;
  } catch {
    return url;
  }
}

export function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

export function formatSuccessRate(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}

// ============================================
// Business rules
// ============================================

export function canDisable(endpoint: WebhookEndpoint): boolean {
  return endpoint.status === 'WEBHOOK_ENDPOINT_STATUS_ACTIVE';
}

export function canEnable(endpoint: WebhookEndpoint): boolean {
  return endpoint.status === 'WEBHOOK_ENDPOINT_STATUS_DISABLED';
}

export function canDelete(endpoint: WebhookEndpoint): boolean {
  return endpoint.status !== 'WEBHOOK_ENDPOINT_STATUS_DELETED';
}

export function canEdit(endpoint: WebhookEndpoint): boolean {
  return endpoint.status !== 'WEBHOOK_ENDPOINT_STATUS_DELETED';
}

export function canTest(endpoint: WebhookEndpoint): boolean {
  return endpoint.status === 'WEBHOOK_ENDPOINT_STATUS_ACTIVE';
}

export function eventTypeCount(endpoint: WebhookEndpoint): string {
  if (endpoint.subscribe_all) return 'All events';
  return `${endpoint.event_types?.length ?? 0} events`;
}
